import { PromoPrizeRewardStatus } from '@daymade/common-lib/dbEnums';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useEffect, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { referralApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

/** When a player has won, they share a prize with their friends */
export interface SharedWinForFriendItem {
  type: 'PLAYER_WIN';
  createdAt: Date;
  otherPlayerFirstNames: string[];
  otherPlayerPrizeName: string;
  otherPlayerPrizeTier: string;
  prizeTier: string;
}

/** When a player's friend has won, they share a prize with the player */
export interface SharedWinFromFriendItem {
  type: 'FRIEND_WIN';
  createdAt: Date;
  otherPlayerFirstName: string;
  otherPlayerPrizeTier: string;
  promoPrizeRewardId: string;
  promoPrizeRewardClaimed: boolean;
  promoPrizeRewardName: string;
  promoPrizeTier: string;
}

export type SharedWinUnion = SharedWinForFriendItem | SharedWinFromFriendItem;

export const useReferralsSharedWins = () => {
  const userStore = useUserStore();
  const [sharedWins, setSharedWins] = useState<SharedWinUnion[] | undefined>(
    undefined,
  );

  useEffect(() => {
    (async () => {
      if (userStore.currentUser?.id) {
        const sharedWinsFromApi = await tryApi(
          referralApiClient.auth.getSharedWins(),
        );

        if (sharedWinsFromApi.status === HttpStatusCode.OK) {
          const fromFriends = sharedWinsFromApi.body.received
            .filter((x) => x.status !== PromoPrizeRewardStatus.CANCELLED)
            .map((x) => ({
              type: 'FRIEND_WIN' as const,
              createdAt: x.createdAt,
              otherPlayerFirstName: x.friendFirstName,
              otherPlayerPrizeTier: x.friendPrizeTierCode,
              promoPrizeRewardId: x.promoPrizeRewardId,
              promoPrizeRewardClaimed:
                x.status === PromoPrizeRewardStatus.CHOSEN ||
                x.status === PromoPrizeRewardStatus.AWARDED,
              promoPrizeRewardName: x.prizeTitle,
              promoPrizeTier: x.promoPrizeOfferCode,
            }));
          const toFriends = sharedWinsFromApi.body.sent.map((x) => ({
            type: 'PLAYER_WIN' as const,
            createdAt: x.createdAt,
            otherPlayerFirstNames: x.friendFirstNames,
            otherPlayerPrizeName: x.friendPrizeTitle,
            otherPlayerPrizeTier: x.friendPromoPrizeOfferCode,
            prizeTier: x.prizeTierCode,
          }));
          setSharedWins(
            (
              fromFriends as (
                | SharedWinForFriendItem
                | SharedWinFromFriendItem
              )[]
            )
              .concat(toFriends)
              // Sort as oldest first, so that they are more visible for people to claim them before they expire
              .sort((x, y) => x.createdAt.getTime() - y.createdAt.getTime()),
          );
        } else if (sharedWinsFromApi.status === 'error') {
          throw new (Error as any)('Failed to get shared wins', {
            cause: sharedWinsFromApi.error,
          });
        }
      }
    })();
  }, [userStore.currentUser?.id]);

  return sharedWins;
};
